import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '../../hooks/useNotifications';
import usePackPermissions from '../../hooks/usePackPermissions';
import useToast from '../../hooks/useToast';
import api from '../../services/api';
import Drawer from './Drawer';

// ─── Icônes SVG inline ────────────────────────────────────────────────────────
const BellIcon = ({ className = 'w-5 h-5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
  </svg>
);

const ReturnIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M8 7h12m0 0l-4-4m4 4l-4 4m0 5H4m0 0l4 4m-4-4l4-4" />
  </svg>
);

const CashIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const DepartureIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const AlertIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
  </svg>
);

const CalendarIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const CheckIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </svg>
);

const SpinnerIcon = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={`animate-spin ${className}`} fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const SpeedometerIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M12 2a10 10 0 100 20A10 10 0 0012 2z" />
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M8.5 14.5l3-5 3.5 2.5" />
    <circle cx="12" cy="12" r="1" fill="currentColor" stroke="none" />
  </svg>
);

// ─── Icône carte de crédit (financement) ─────────────────────────────────────
const CreditCardIcon = ({ className = 'w-4 h-4' }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round"
      d="M3 10h18M7 15h.01M11 15h2M5 5h14a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2z" />
  </svg>
);

// ─── Config par type : badge, icône, couleurs, description i18n ──────────────
const TYPE_CFG = {
  RETOUR_J: {
    ring:    'bg-orange-100',
    icon:    ReturnIcon,
    color:   'text-orange-600',
    badge:   'bg-orange-100 text-orange-700',
    border:  'border-orange-300',
    labelKey: 'notifications.returnToday',
    descKey:  'notifications.returnTodayDesc',
  },
  ACTIVATION_J: {
    ring:    'bg-green-100',
    icon:    DepartureIcon,
    color:   'text-green-600',
    badge:   'bg-green-100 text-green-700',
    border:  'border-green-300',
    labelKey: 'notifications.activationToday',
    descKey:  'notifications.activationTodayDesc',
  },
  RAPPEL_J1: {
    ring:    'bg-amber-100',
    icon:    AlertIcon,
    color:   'text-amber-600',
    badge:   'bg-amber-100 text-amber-700',
    border:  'border-amber-300',
    labelKey: 'notifications.criticalReminder',
    descKey:  'notifications.criticalReminderDesc',
  },
  PREP_J2: {
    ring:    'bg-blue-100',
    icon:    CalendarIcon,
    color:   'text-blue-600',
    badge:   'bg-blue-100 text-blue-700',
    border:  'border-blue-300',
    labelKey: 'notifications.prepJ2',
    descKey:  'notifications.prepJ2Desc',
  },
  PREP_J3: {
    ring:    'bg-indigo-100',
    icon:    CalendarIcon,
    color:   'text-indigo-600',
    badge:   'bg-indigo-100 text-indigo-700',
    border:  'border-indigo-300',
    labelKey: 'notifications.prepJ3',
    descKey:  'notifications.prepJ3Desc',
  },
  PAIEMENT_RETOUR_J: {
    ring:    'bg-red-100',
    icon:    CashIcon,
    color:   'text-red-600',
    badge:   'bg-red-100 text-red-700',
    border:  'border-red-400',
    labelKey: 'notifications.paiementRetour',
    descKey:  'notifications.paiementRetourDesc',
  },
  // ── Financement véhicules ────────────────────────────────────────────────
  TRAITE_RETARD: {
    ring:    'bg-red-100',
    icon:    CreditCardIcon,
    color:   'text-red-600',
    badge:   'bg-red-100 text-red-700',
    border:  'border-red-400',
    labelKey: 'notifications.traiteRetard',
    descKey:  'notifications.traiteRetardDesc',
  },
  TRAITE_RAPPEL: {
    ring:    'bg-orange-100',
    icon:    CreditCardIcon,
    color:   'text-orange-600',
    badge:   'bg-orange-100 text-orange-700',
    border:  'border-orange-300',
    labelKey: 'notifications.traiteRappel',
    descKey:  'notifications.traiteRappelDesc',
  },
  TRAITE_DU_MOIS: {
    ring:    'bg-amber-100',
    icon:    CreditCardIcon,
    color:   'text-amber-600',
    badge:   'bg-amber-100 text-amber-700',
    border:  'border-amber-300',
    labelKey: 'notifications.traiteDuMois',
    descKey:  'notifications.traiteDuMoisDesc',
  },
  FINANCEMENT_TERMINE: {
    ring:    'bg-green-100',
    icon:    CreditCardIcon,
    color:   'text-green-600',
    badge:   'bg-green-100 text-green-700',
    border:  'border-green-300',
    labelKey: 'notifications.financementTermine',
    descKey:  'notifications.financementTermineDesc',
  },
};

// ─── Badge rouge compteur ─────────────────────────────────────────────────────
const RedBadge = ({ count }) => (
  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center leading-none shadow-sm">
    {count > 99 ? '99+' : count}
  </span>
);

const FIN_TYPES = new Set(['TRAITE_RETARD', 'TRAITE_RAPPEL', 'TRAITE_DU_MOIS', 'FINANCEMENT_TERMINE']);

// ─── Carte notification standard (Pro / Pro+) ────────────────────────────────
const NotifCard = ({ notification: n, onTerminer, onViewReservation, t }) => {
  const [loading, setLoading] = useState(false);
  const cfg      = TYPE_CFG[n.type] ?? TYPE_CFG.PREP_J3;
  const IconComp = cfg.icon;
  const isFinancement = FIN_TYPES.has(n.type);

  const fmtDate = d =>
    d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';

  const handleClick = async () => {
    setLoading(true);
    await onTerminer(n);
    setLoading(false);
  };

  return (
    <div className={`bg-white rounded-2xl p-4 border shadow-sm hover:shadow-md transition-shadow ${n.isNew ? `border-l-[3px] ${cfg.border}` : 'border-gray-100'}`}>

      {/* En-tête : icône + client/banque + badge type */}
      <div className="flex items-start gap-3 mb-2">
        <div className={`w-9 h-9 rounded-full ${cfg.ring} flex items-center justify-center flex-shrink-0`}>
          <IconComp className={`w-4 h-4 ${cfg.color}`} />
        </div>
        <div className="min-w-0 flex-1">
          {isFinancement ? (
            <>
              <p className="text-sm font-bold text-gray-800 truncate">{n.vehicule || '—'}</p>
              <p className="text-xs text-gray-500 truncate">{n.client || '—'}</p>
            </>
          ) : (
            <>
              <p className="text-sm font-bold text-gray-800 truncate">{n.client || '—'}</p>
              <p className="text-xs text-gray-500 truncate">{n.vehicule || '—'}</p>
            </>
          )}
        </div>
        <span className={`px-2 py-0.5 rounded-lg ${cfg.badge} text-[10px] font-semibold flex-shrink-0 whitespace-nowrap`}>
          {t(cfg.labelKey)}
        </span>
      </div>

      {/* Description contextuelle */}
      <p className="text-xs text-gray-500 mb-3 leading-relaxed pl-12">
        {t(cfg.descKey)}
      </p>

      {/* Infos dates/montant selon le type */}
      {isFinancement ? (
        <div className="grid grid-cols-2 gap-2 mb-2">
          <div className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-[10px] text-gray-400 mb-0.5">Échéance</p>
            <p className="text-xs font-semibold text-gray-700">{fmtDate(n.dateFin)}</p>
          </div>
          <div className="bg-gray-50 rounded-xl px-3 py-2">
            <p className="text-[10px] text-gray-400 mb-0.5">Montant traite</p>
            <p className={`text-xs font-bold ${cfg.color}`}>
              {Number(n.resteAPayer).toLocaleString('fr-MA')} MAD
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2 mb-3">
          {[
            [t('notifications.dateStart'), fmtDate(n.dateDebut)],
            [t('notifications.dateEnd'),   fmtDate(n.dateFin)],
          ].map(([label, value]) => (
            <div key={label} className="bg-gray-50 rounded-xl px-3 py-2">
              <p className="text-[10px] text-gray-400 mb-0.5">{label}</p>
              <p className="text-xs font-semibold text-gray-700 truncate">{value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Montant restant à récupérer (paiement partiel réservation) */}
      {n.type === 'PAIEMENT_RETOUR_J' && n.resteAPayer > 0 && (
        <div className="mb-3 space-y-2">
          <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-xl px-3 py-2">
            <span className="text-xs text-red-600 font-medium">{t('notifications.resteARecuperer')}</span>
            <span className="text-sm font-bold text-red-700">{n.resteAPayer} MAD</span>
          </div>
          <button
            onClick={() => onViewReservation(n)}
            className="w-full py-2 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-2"
          >
            <CashIcon className="w-3.5 h-3.5" />
            {t('notifications.encaisserSolde')}
          </button>
        </div>
      )}

      {/* Bouton "Terminer retour" uniquement sur les retours du jour */}
      {n.type === 'RETOUR_J' && (
        <button
          onClick={handleClick}
          disabled={loading}
          className="w-full py-2 rounded-xl bg-[#1F6B57] hover:bg-[#1F6B57] disabled:opacity-60 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-2"
        >
          {loading ? <SpinnerIcon /> : <CheckIcon />}
          {t('notifications.finishReturn')}
        </button>
      )}
    </div>
  );
};

// ─── Carte KM (Pro / Pro+) — séparée, uniquement sur RETOUR_J ────────────────
const KmUpdateCard = ({ notification: n, t }) => {
  const toast    = useToast();
  const inputRef = useRef(null);

  const [vehicle,  setVehicle]  = useState(null);
  const [loaded,   setLoaded]   = useState(false);
  const [editing,  setEditing]  = useState(false);
  const [km,       setKm]       = useState('');
  const [saving,   setSaving]   = useState(false);

  // Récupère les données complètes du véhicule pour afficher le km de départ
  useEffect(() => {
    api.get('/Vehicules', { params: { page: 1, pageSize: 200 } })
      .then(({ data }) => {
        const list = data.items ?? data ?? [];
        let found = null;

        // 1. Par vehiculeId exact (si le backend le renvoie)
        if (n.vehiculeId) {
          found = list.find(v => v.id === n.vehiculeId);
        }

        // 2. Matching souple sur n.vehicule (marque en début de chaîne)
        if (!found && n.vehicule) {
          const q = n.vehicule.trim().toLowerCase();
          found = list.find(v => {
            const marque = (v.marque || '').toLowerCase();
            const mat    = (v.matricule || '').toLowerCase();
            return q === marque || q.startsWith(marque) || marque.startsWith(q) || q === mat;
          });
        }

        // 3. Matching par mot-clé (fallback robuste)
        if (!found && n.vehicule) {
          const words = n.vehicule.toLowerCase().split(/[\s\-–_/]+/).filter(w => w.length >= 2);
          found = list.find(v =>
            words.some(w => (v.marque || '').toLowerCase().includes(w))
          );
        }

        setVehicle(found ?? null);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenEdit = () => {
    setKm('');
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 40);
  };

  const handleSave = async () => {
    if (!vehicle || !km) return;
    setSaving(true);
    try {
      const newKm = parseInt(km, 10) || 0;

      // 1. Mise à jour du véhicule
      await api.patch(`/Vehicules/${vehicle.id}`, {
        matricule:        vehicle.matricule         || '',
        marque:           vehicle.marque,
        modele:           vehicle.modele,
        couleur:          vehicle.couleur,
        kilometrage:      newKm,
        numeroCarteGrise: vehicle.numeroCarteGrise  || '',
        prixParJour:      vehicle.prixParJour,
        statut:           vehicle.statut            || 0,
        typeCarburant:    vehicle.typeCarburant      || 0,
        transmission:     vehicle.transmission      || 0,
        imageUrl:         vehicle.imageUrl          || '',
      });

      // 2. Mise à jour de la dernière vidange du même véhicule
      try {
        const { data: vidangesData } = await api.get('/Vidange', { params: { kmAvantAlerte: 5000, page: 1, pageSize: 100 } });
        const vehicleVidanges = (vidangesData.items ?? [])
          .filter(v => v.vehiculeId === vehicle.id)
          .sort((a, b) => new Date(b.dateVidange) - new Date(a.dateVidange));
        if (vehicleVidanges.length > 0) {
          await api.patch(`/Vidange/${vehicleVidanges[0].id}`, { KilometrageActuel: newKm });
        }
      } catch { /* silence — ne bloque pas la mise à jour principale */ }

      setVehicle(prev => ({ ...prev, kilometrage: newKm }));
      toast.success(t('notifications.kmUpdateSuccess'));
      setEditing(false);
    } catch {
      toast.error(t('notifications.kmUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const currentKm = vehicle?.kilometrage;

  return (
    <div className="bg-white rounded-2xl p-4 border border-sky-200 border-l-[3px] border-l-sky-400 shadow-sm">

      {/* En-tête */}
      <div className="flex items-start gap-3 mb-3">
        <div className="w-9 h-9 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
          <SpeedometerIcon className="w-4 h-4 text-sky-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold text-gray-800 truncate">{n.client || '—'}</p>
          <p className="text-xs text-gray-500 truncate">{n.vehicule || '—'}</p>
        </div>
        <span className="px-2 py-0.5 rounded-lg bg-sky-100 text-sky-700 text-[10px] font-semibold flex-shrink-0 whitespace-nowrap">
          {t('notifications.kmLabel')}
        </span>
      </div>

      {/* KM de départ */}
      <div className="flex items-center justify-between bg-sky-50 border border-sky-200 rounded-xl px-3 py-2 mb-3">
        <span className="text-xs text-sky-600 font-medium">{t('notifications.kmDepart', 'Km de départ')}</span>
        <span className="text-sm font-bold text-sky-700">
          {!loaded
            ? <SpinnerIcon className="w-3.5 h-3.5 text-sky-400" />
            : currentKm != null
              ? `${currentKm.toLocaleString('fr-FR')} km`
              : <span className="text-gray-400 text-xs">—</span>
          }
        </span>
      </div>

      {/* Champ inline ou bouton */}
      {editing ? (
        <div className="flex gap-2 items-center">
          <input
            ref={inputRef}
            type="number"
            min="0"
            value={km}
            onChange={e => setKm(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            className="flex-1 px-3 py-2 border border-sky-300 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-sky-400 focus:border-transparent"
            placeholder={t('notifications.kmPlaceholder')}
          />
          <button
            onClick={handleSave}
            disabled={saving || !km}
            className="px-3 py-2 rounded-xl bg-sky-600 hover:bg-sky-700 disabled:opacity-60 text-white text-xs font-semibold transition-colors flex items-center gap-1.5 flex-shrink-0"
          >
            {saving ? <SpinnerIcon className="w-3 h-3" /> : <CheckIcon className="w-3 h-3" />}
            {t('common.save')}
          </button>
          <button
            onClick={() => setEditing(false)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-gray-500 hover:bg-gray-50 text-xs font-semibold flex-shrink-0"
          >
            {t('common.cancel')}
          </button>
        </div>
      ) : (
        <button
          onClick={handleOpenEdit}
          disabled={!vehicle}
          className="w-full py-2 rounded-xl bg-sky-600 hover:bg-sky-700 disabled:opacity-50 text-white text-xs font-semibold transition-colors flex items-center justify-center gap-2"
        >
          <SpeedometerIcon className="w-3.5 h-3.5" />
          {t('notifications.updateKm')}
        </button>
      )}
    </div>
  );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const NotificationBell = () => {
  const { t }                                          = useTranslation();
  const navigate                                       = useNavigate();
  const toast                                          = useToast();
  const { pack }                                       = usePackPermissions();
  const { notifications, count, refresh, markAsSeen } = useNotifications();
  const [open, setOpen]                                = useState(false);
  const popoverRef                                     = useRef(null);

  const isStart     = pack === 'Start';
  const isProOrPlus = pack === 'Pro' || pack === 'ProPlus';

  // Fermeture popover Start au clic dehors
  useEffect(() => {
    if (!isStart || !open) return;
    const handler = e => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isStart, open]);

  const handleOpen = () => {
    if (count === 0 && notifications.length === 0) return;
    refresh();
    if (count > 0) markAsSeen(); // compteur → 0 dès la consultation
    setOpen(true);
  };

  const handleViewReservation = (notif) => {
    setOpen(false);
    navigate('/reservations', { state: { openReservationId: notif.id } });
  };

  /**
   * Clôture un retour :
   *  1. PATCH reservation → Terminee (statut = 3)
   *     Le service vérifie les conflits et remet le véhicule en Disponible
   *     uniquement si aucune autre réservation active ne le réclame.
   *  2. Rafraîchit les notifications (la carte RETOUR_J disparaît).
   *  3. Redirige vers /cautions pour finaliser les dépôts.
   */
  const handleTerminerRetour = async (notif) => {
    try {
      await api.patch(`/Reservation/${notif.id}`, { statut: 3 }); // 3 = Terminee
      toast.success(t('notifications.terminerSuccess'));
      await refresh();
      setOpen(false);
      navigate('/cautions');
    } catch {
      toast.error(t('notifications.terminerError'));
    }
  };

  // SuperAdmin ou sans pack → rien
  if (!isStart && !isProOrPlus) return null;

  // ── Pack Start : popover résumé minimaliste ───────────────────────────────
  if (isStart) {
    return (
      <div className="relative" ref={popoverRef}>
        <button
          onClick={handleOpen}
          className="relative w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
          title={t('notifications.title')}
          aria-label={t('notifications.title')}
        >
          <BellIcon />
          {count > 0 && <RedBadge count={count} />}
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              key="notif-popover"
              initial={{ opacity: 0, y: -8, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.95 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden"
            >
              <div className="flex items-center gap-2.5 px-4 py-3 border-b border-gray-50">
                <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <BellIcon className="w-4 h-4 text-orange-500" />
                </div>
                <p className="text-sm font-semibold text-gray-800">{t('notifications.title')}</p>
              </div>

              <div className="px-4 py-4 space-y-2">
                {notifications.map((n, idx) => {
                  const cfg = TYPE_CFG[n.type] ?? TYPE_CFG.PREP_J3;
                  return (
                    <div key={idx} className={`flex items-center gap-2.5 rounded-xl px-3 py-2.5 ${cfg.ring}`}>
                      <cfg.icon className={`w-4 h-4 ${cfg.color} flex-shrink-0`} />
                      <div className="min-w-0">
                        <p className={`text-xs font-semibold ${cfg.color}`}>{t(cfg.labelKey)}</p>
                        <p className="text-xs text-gray-600 truncate">{n.client} — {n.vehicule}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ── Pack Pro / Pro+ : cloche + drawer détaillé ────────────────────────────
  return (
    <>
      <button
        onClick={handleOpen}
        className="relative w-9 h-9 rounded-xl flex items-center justify-center text-gray-500 hover:bg-gray-100 transition-colors"
        title={t('notifications.title')}
        aria-label={t('notifications.title')}
      >
        <BellIcon />
        {count > 0 && <RedBadge count={count} />}
      </button>

      <Drawer
        isOpen={open}
        onClose={() => setOpen(false)}
        title={t('notifications.title')}
        subtitle={
          notifications.length > 0
            ? t('notifications.subtitle', { count: notifications.length })
            : t('notifications.noNotifications')
        }
        size="md"
      >
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
              <BellIcon className="w-7 h-7 text-gray-300" />
            </div>
            <p className="text-sm font-medium">{t('notifications.noNotifications')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {notifications.flatMap((n, idx) => {
              const cards = [
                <NotifCard
                  key={`notif-${idx}`}
                  notification={n}
                  onTerminer={handleTerminerRetour}
                  onViewReservation={handleViewReservation}
                  t={t}
                />,
              ];
              if (n.type === 'RETOUR_J') {
                cards.push(
                  <KmUpdateCard key={`km-${idx}`} notification={n} t={t} />
                );
              }
              return cards;
            })}
          </div>
        )}
      </Drawer>
    </>
  );
};

export default NotificationBell;





